import levels from '../data/levels.json';
import type { LevelDefinition } from '../types/gamification';

const LEVEL_LIST = levels as LevelDefinition[];

export function calculateXP(baseXP: number, stars: 1 | 2 | 3, currentStreak: number): number {
  let xp = baseXP;

  // Star multiplier
  if (stars === 2) xp = Math.round(xp * 1.25);
  if (stars === 3) xp = Math.round(xp * 1.5);

  // Streak bonus
  if (currentStreak >= 7) xp = Math.round(xp * 1.25);
  else if (currentStreak >= 3) xp = Math.round(xp * 1.1);

  return xp;
}

/** Coerce any non-finite/missing XP value to 0 so downstream math never
 *  produces NaN — a single undefined field on a legacy persisted child
 *  should never render a broken "NaN/100" progress bar. */
function safeXP(totalXP: number): number {
  return Number.isFinite(totalXP) && totalXP >= 0 ? totalXP : 0;
}

export function getLevelForXP(totalXP: number): LevelDefinition {
  const xp = safeXP(totalXP);
  let current = LEVEL_LIST[0];
  for (const level of LEVEL_LIST) {
    if (xp >= level.xpRequired) {
      current = level;
    } else {
      break;
    }
  }
  return current;
}

export function getXPToNextLevel(totalXP: number): { current: number; next: number; progress: number } {
  const xp = safeXP(totalXP);
  const currentLevel = getLevelForXP(xp);
  const nextLevel = LEVEL_LIST.find(l => l.level === currentLevel.level + 1);

  if (!nextLevel) {
    return { current: xp, next: xp, progress: 1 };
  }

  const progressXP = xp - currentLevel.xpRequired;
  const neededXP = nextLevel.xpRequired - currentLevel.xpRequired;
  return {
    current: progressXP,
    next: neededXP,
    progress: Math.min(progressXP / neededXP, 1),
  };
}

export function checkLevelUp(oldXP: number, newXP: number): LevelDefinition | null {
  const oldLevel = getLevelForXP(oldXP);
  const newLevel = getLevelForXP(newXP);
  if (newLevel.level > oldLevel.level) return newLevel;
  return null;
}

export function getAllLevels(): LevelDefinition[] {
  return LEVEL_LIST;
}
